import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { API_CONFIG, API_LIST } from '../../Constants/ApiDefinations'
import { TypeObject } from '../../Models'
import mainApiService from '../../Services'
import { setLoader } from './loader.slice'

const settingKeys: any = {
  buildConfig: 'buildDetailService',
  manageFileConfig: 'manageFile',
  bulkUpdateConfig: 'bulkUpdate',
  multiAudioConfig: 'languageVariantFeature',
  managePolicyConfig: 'featureControlConfig',
  cmsConfig: 'cmsConfig',

  buildDetailService: 'buildConfig',
  manageFile: 'manageFileConfig',
  bulkUpdate: 'bulkUpdateConfig',
  languageVariantFeature: 'multiAudioConfig',
  featureControlConfig: 'managePolicyConfig',
}

const cmsConfigInitialState: TypeObject = Object.freeze({
  LIVE_SPORT_DURATION_TIME: 16,
  ENABLED_FEATURES: {
    NOTIFICATIONS: true,
    HOME_BAR_CHART: true,
    ASSET_DEFINATION_CLONE: true,
    ASSET_DEFINATION_EXPORT: true,
    BULK_UPDATE_ADV_CUEPOINT_SU: true,
    BULK_UPDATE_CUEPOINT_SU: true,
    BULK_UPDATE_ENCODED_FEED: true,
    BULK_UPDATE_SRT_FEED: true,
  },
})

/**
 * @description
 * 1. Fetches application configuration for sidebar, header, and other UI elements from CONTENT_CATEGORY and AUDIT_TRAILS masters using the API `/attributeRepository/systemConfigurations/action/list?keyName=' + key`.
 *    This includes:
 *    - Labels (`appLabels`)
 *    - Content categories (`contentCategories`)
 *    - Audit trails (`auditTrails`)
 *    - `appConfigStatus`: Tracks the status of fetching app configuration (`idle`, `loading`, `succeeded`, `failed`).
 *    - `appConfigError`: Stores any error message encountered while fetching app configuration.
 *
 * 2. Fetches application configuration settings from the Settings table in the backend.
 *    This includes:
 *    - Bulk update configuration (`bulkUpdateConfig`)
 *    - Manage File configuration (`manageFileConfig`)
 *    - Multi-audio configuration (`multiAudioConfig`)
 *    - Policy management configuration like geo policy (`managePolicyConfig`)
 *    - CMS configuration for all FE congifiguartions (`cmsConfig`)
 *    - Build configuration for build version (`buildConfig`)
 *    - `appConfigSettingsStatus`: Tracks the status of fetching app configuration settings (`idle`, `loading`, `succeeded`, `failed`).
 *    - `appConfigSettingsError`: Stores any error message encountered while fetching app configuration settings.
 */
export const appConfigInitialState: {
  appLabels: TypeObject
  contentCategories: TypeObject[]
  auditTrails: TypeObject[]
  buildConfig: {
    version: string
    releaseDate: string
  } | null
  bulkUpdateConfig: TypeObject | null
  manageFileConfig: TypeObject | null
  managePolicyConfig: TypeObject | null
  multiAudioConfig: TypeObject | null
  cmsConfig: TypeObject

  appConfigStatus: string
  appConfigError: string

  appConfigSettingsStatus: string
  appConfigSettingsError: string
} = {
  appLabels: <TypeObject>{},
  contentCategories: [],
  auditTrails: [],
  buildConfig: null,
  bulkUpdateConfig: null,
  manageFileConfig: null,
  managePolicyConfig: null,
  multiAudioConfig: null,
  cmsConfig: cmsConfigInitialState,
  appConfigStatus: 'idle',
  appConfigError: '',
  appConfigSettingsStatus: 'idle',
  appConfigSettingsError: '',
}

const appConfigSlice = createSlice({
  name: 'appConfig',
  initialState: appConfigInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAppConfig.pending, (state) => {
        state.appConfigStatus = 'loading'
      })
      .addCase(getAppConfig.fulfilled, (state, action) => {
        state.appConfigStatus = 'succeeded'
        if (action?.payload?.data?.length) {
          action.payload.data.forEach((data: TypeObject) => {
            data.configValue?.forEach((item: TypeObject) => {
              state.appLabels = { ...state.appLabels, [`${item.key}`]: item.value }
            })
            if (data.configKey === 'AUDIT_TRAILS') {
              state.auditTrails = data.configValue
            } else if (data.configKey === 'CONTENT_CATEGORY') {
              state.contentCategories = data.configValue
            }
          })
        }
      })
      .addCase(getAppConfig.rejected, (state, action) => {
        state.appConfigStatus = 'failed'
        state.appConfigError = action.error.message || ''
      })
      .addCase(getAppConfigSettings.pending, (state) => {
        state.appConfigSettingsStatus = 'loading'
      })
      .addCase(getAppConfigSettings.fulfilled, (state, action) => {
        state.appConfigSettingsStatus = 'succeeded'
        if (action?.payload?.data) {
          Object.keys(settingKeys).forEach((key) => {
            if (action.payload.data?.[key] && state) {
              if (key === settingKeys.cmsConfig) {
                action.payload.data[key] = {
                  ...cmsConfigInitialState,
                  ...action.payload.data[key],
                  ENABLED_FEATURES: {
                    ...cmsConfigInitialState.ENABLED_FEATURES,
                    ...action.payload.data[key]?.ENABLED_FEATURES,
                  },
                }
              }
              state[key as keyof typeof state] = action.payload.data[key]
            }
          })
        }
      })
      .addCase(getAppConfigSettings.rejected, (state, action) => {
        state.appConfigSettingsStatus = 'failed'
        state.appConfigSettingsError = action.error.message || ''
      })
  },
})

export default appConfigSlice

export const getAppConfig = createAsyncThunk('appConfig/getAppConfig', async () => {
  const requestedData = API_CONFIG(
    API_LIST.SYSTEM_CONFIGURATION.MASTER_LIST,
    {},
    null,
    'keyName=CONTENT_CATEGORY,AUDIT_TRAILS',
  )
  const data = await mainApiService(requestedData)
  return data
})

export const getAppConfigSettings = createAsyncThunk(
  'appConfig/getAppConfigSettings',
  async (config: string[] | any, { dispatch, getState }) => {
    const state = getState() as any
    const cachedData: Record<string, any> = state.appConfig || {}

    const { settings, showLoader } = config

    // Split keys into cached and uncached
    const cachedKeys: Record<string, any> = {}
    const keysToFetch: string[] = []
    settings.forEach((key: string) => {
      if (cachedData[key] && key !== settingKeys.cmsConfig) {
        cachedKeys[key] = cachedData[key]
      } else {
        keysToFetch.push(settingKeys[key])
      }
    })

    const fetchedData: Record<string, any> = {}
    if (keysToFetch.length > 0) {
      if (showLoader) {
        dispatch(setLoader(true))
      }
      const requestedData = API_CONFIG(
        API_LIST.PERMISSION_ROLE.APP_CONFIG,
        null,
        {},
        {
          configKeys: keysToFetch.join(',').toString(),
        },
      )

      const response = await mainApiService(requestedData)

      if (response?.data?.length) {
        response.data.forEach((data: TypeObject) => {
          if (settingKeys[data.feature]) {
            fetchedData[settingKeys[data.feature]] = data.configs
          }
        })
      }
      if (showLoader) {
        dispatch(setLoader(false))
      }
    }

    return {
      message: 'Settings Data Fetched Successfully',
      responseCode: 200,
      data: { ...cachedKeys, ...fetchedData },
    }
  },
)

const appConfigSelector = (state: any) => state.appConfig

export const selectContentCategories = createSelector(
  appConfigSelector,
  (state) => state.contentCategories,
)

export const selectManageFileConfig = createSelector(
  appConfigSelector,
  (state) => state.manageFileConfig,
)

export const selectBulkUpdateConfig = createSelector(
  appConfigSelector,
  (state) => state.bulkUpdateConfig,
)

export const selectMultiAudioConfig = createSelector(
  appConfigSelector,
  (state) => state.multiAudioConfig,
)
